import { useState } from 'react';
import { useHistory } from 'react-router-dom';
import {
  TableContainer,
  Paper,
  Table,
  TableCell,
  TableHead,
  TableRow,
  TableBody,
  IconButton,
  Menu,
  MenuItem,
  Tooltip,
} from '@material-ui/core';

import { ExitToApp as ExitToAppIcon, MoreVertRounded as MoreVertIcon } from '@material-ui/icons';
import { OrganizationMember } from '../../types/organization.type';
import { ADMIN_USERS_APP_PATHS } from '../../../users/constants/users-paths.constants';
import { ORGANIZATION_CREATE_ORDER_PATH } from '../../constants/organizations-paths.constants';
import RemoveMemberDialog from '../Modals/RemoveMemberDialog.component';
import { useUpdateOrganizationMembers } from '../../hooks/update-organization-members.hook';
import { formatPhoneNumber } from '../../../shared/utils/format-phone-number.util';

type MembersTableHeader = {
  key: string;
  label?: string;
  minWidth: number;
  align: 'left' | 'right';
};

type Props = {
  organizationId: string;
  maxHeight: string;
  members: OrganizationMember[];
  hasDeliveryPoints: boolean;
  refreshOrganization: () => Promise<void>;
};

const MEMBERS_TABLE_HEADERS: MembersTableHeader[] = [
  { key: 'id', minWidth: 65, align: 'left' },
  { key: 'firstName', label: 'Nom', minWidth: 255, align: 'left' },
  { key: 'email', label: 'Email', minWidth: 255, align: 'left' },
  { key: 'phone', label: 'Téléphone', minWidth: 140, align: 'left' },
  { key: 'ordersCount', label: 'Commandes', minWidth: 125, align: 'right' },
  { key: 'actions', minWidth: 65, align: 'right' },
];

function MembersTableHeader({ headers }: { headers: MembersTableHeader[] }) {
  return (
    <TableHead>
      <TableRow>
        {headers.map((header) => (
          <TableCell key={header.key} align={header.align} style={{ minWidth: header.minWidth, fontWeight: 'bold' }}>
            {header?.label ?? ''}
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

function MembersTableEmptyBody({ headers }: { headers: MembersTableHeader[] }) {
  return (
    <TableBody>
      <TableRow>
        <TableCell colSpan={headers.length} align="center">
          Aucun membre
        </TableCell>
      </TableRow>
    </TableBody>
  );
}

function MembersTableBody({
  organizationId,
  members,
  hasDeliveryPoints,
  refreshOrganization,
}: {
  organizationId: string;
  members: OrganizationMember[];
  hasDeliveryPoints: boolean;
  refreshOrganization: () => Promise<void>;
}) {
  return (
    <TableBody>
      {members.map((member) => (
        <MemberTableRow
          key={member.id}
          organizationId={organizationId}
          member={member}
          hasDeliveryPoints={hasDeliveryPoints}
          refreshOrganization={refreshOrganization}
        />
      ))}
    </TableBody>
  );
}

function MemberTableRow({
  organizationId,
  member,
  hasDeliveryPoints,
  refreshOrganization,
}: {
  organizationId: string;
  member: OrganizationMember;
  hasDeliveryPoints: boolean;
  refreshOrganization: () => Promise<void>;
}) {
  const href = ADMIN_USERS_APP_PATHS.USER_DETAILS(member.id);
  const history = useHistory();

  const { updateOrganizationMembers, isUpdating: isUpdatingMembers } = useUpdateOrganizationMembers();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => setAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);

  const handleNewOrder = () => {
    history.push(ORGANIZATION_CREATE_ORDER_PATH(organizationId, member.id));
  };

  const handleRemoveMember = async () => {
    const updatedOrganization = await updateOrganizationMembers({
      organizationId,
      members: {
        remove: [member.id],
      },
    });

    if (updatedOrganization) await refreshOrganization();
  };

  return (
    <TableRow key={member.id} hover>
      <TableCell>
        <IconButton size="small" aria-label="show" target="_blank" href={href}>
          <ExitToAppIcon />
        </IconButton>
      </TableCell>
      <TableCell>
        {member.firstname} {member.lastname}
      </TableCell>
      <TableCell>{member.email}</TableCell>
      <TableCell>{formatPhoneNumber(member.phone)}</TableCell>
      <TableCell align="right">0</TableCell>
      <TableCell align="right">
        <IconButton size="small" aria-controls="simple-menu" aria-haspopup="true" onClick={handleClick}>
          <MoreVertIcon />
        </IconButton>
        <Menu id="member-actions" anchorEl={anchorEl} keepMounted open={Boolean(anchorEl)} onClose={handleClose}>
          <RemoveMemberDialog
            member={member}
            onConfirm={handleRemoveMember}
            trigger={(handleOpen) => (
              <MenuItem onClick={handleOpen} disabled={isUpdatingMembers}>
                Supprimer
              </MenuItem>
            )}
          />
          <Tooltip title={hasDeliveryPoints ? '' : 'Aucun point de livraison'} disableHoverListener={hasDeliveryPoints}>
            <span>
              <MenuItem onClick={handleNewOrder} disabled={isUpdatingMembers || !hasDeliveryPoints}>
                Nouvelle commande
              </MenuItem>
            </span>
          </Tooltip>
        </Menu>
      </TableCell>
    </TableRow>
  );
}

export default function MembersTable({ maxHeight, members, organizationId, hasDeliveryPoints, refreshOrganization }: Props) {
  const hasMembers = !!members.length;

  return (
    <Paper>
      <TableContainer style={{ maxHeight }}>
        <Table stickyHeader>
          <MembersTableHeader headers={MEMBERS_TABLE_HEADERS} />
          {!hasMembers && <MembersTableEmptyBody headers={MEMBERS_TABLE_HEADERS} />}
          {hasMembers && (
            <MembersTableBody
              refreshOrganization={refreshOrganization}
              members={members}
              organizationId={organizationId}
              hasDeliveryPoints={hasDeliveryPoints}
            />
          )}
        </Table>
      </TableContainer>
    </Paper>
  );
}
